import React, { useState } from "react";
import {
  Box,
  Card,
  Avatar,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  Snackbar,
  Alert,
  Skeleton,
  Stack,
  useTheme,
  alpha,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SendIcon from "@mui/icons-material/Send";
import {
  useGetTicketsQuery,
  useGetTicketQuery,
  useReplyTicketMutation,
  useUpdateTicketStatusMutation,
} from "state/api";
import {
  PageContainer,
  TableToolbar,
  StatusChip,
  formatDateTime,
} from "components/shared";

const TicketCard = ({ ticket, onView }) => {
  const name = ticket?.customer?.name || ticket?.contact?.name || "Guest";
  const email = ticket?.customer?.email || ticket?.contact?.email || "";
  return (
    <Card sx={{ p: 2 }}>
      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
        <Avatar sx={{ bgcolor: "primary.main" }}>
          {name.charAt(0).toUpperCase()}
        </Avatar>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 1,
              alignItems: "center",
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography fontWeight={600} noWrap>
                {name}
              </Typography>
              <Typography variant="caption" color="text.secondary" noWrap>
                {email}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              {ticket?.ticketNumber}
            </Typography>
          </Box>

          <Typography variant="body2" sx={{ mt: 1 }}>
            {ticket?.subject}
          </Typography>

          <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap" }}>
            <StatusChip status={ticket?.priority} />
            <StatusChip status={ticket?.status} />
            {ticket?.type && (
              <StatusChip status="inactive" label={ticket.type} />
            )}
          </Stack>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 1,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {formatDateTime(ticket?.createdAt)}
            </Typography>
            <IconButton size="small" onClick={() => onView(ticket)}>
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

const SupportTicketList = () => {
  const theme = useTheme();
  const [search, setSearch] = useState("");
  const [priority, setPriority] = useState("All");
  const [status, setStatus] = useState("All");

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [replyBody, setReplyBody] = useState("");
  const [toast, setToast] = useState(null);

  const { data, isLoading } = useGetTicketsQuery({ priority, status, search });
  const [replyTicket, { isLoading: isReplying }] = useReplyTicketMutation();
  const [updateTicketStatus, { isLoading: isUpdatingStatus }] =
    useUpdateTicketStatusMutation();

  const tickets = data?.data || [];

  const { data: ticketData } = useGetTicketQuery(selectedTicket?._id, {
    skip: !selectedTicket?._id || !openDialog,
  });
  const activeTicket = ticketData?.data || selectedTicket;
  const messages = activeTicket?.messages || [];

  const handleView = (t) => {
    setSelectedTicket(t);
    setReplyBody("");
    setOpenDialog(true);
  };
  const handleClose = () => {
    setOpenDialog(false);
    setSelectedTicket(null);
    setReplyBody("");
  };

  const handleSendReply = async () => {
    const body = replyBody.trim();
    if (!body || !activeTicket?._id) return;
    try {
      await replyTicket({ id: activeTicket._id, body }).unwrap();
      setReplyBody("");
      setToast({ severity: "success", msg: "Reply sent." });
    } catch (err) {
      setToast({ severity: "error", msg: err?.data?.message || "Failed to send reply" });
    }
  };

  const handleStatusChange = async (e) => {
    const next = e.target.value;
    if (!activeTicket?._id) return;
    try {
      await updateTicketStatus({ id: activeTicket._id, status: next }).unwrap();
      setToast({ severity: "success", msg: `Status updated to ${next}.` });
    } catch (err) {
      setToast({ severity: "error", msg: err?.data?.message || "Failed to update status" });
    }
  };

  return (
    <PageContainer
      title="Support Tickets"
      subtitle="Respond to customer support requests"
      maxWidth={1000}
    >
      <TableToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder="Search by subject…"
        filters={[
          {
            label: "Priority",
            value: priority,
            onChange: setPriority,
            options: [
              { label: "All Priorities", value: "All" },
              { label: "High", value: "High" },
              { label: "Medium", value: "Medium" },
              { label: "Low", value: "Low" },
            ],
          },
          {
            label: "Status",
            value: status,
            onChange: setStatus,
            options: [
              { label: "All Statuses", value: "All" },
              { label: "Open", value: "Open" },
              { label: "Pending", value: "Pending" },
              { label: "Closed", value: "Closed" },
            ],
          },
        ]}
      />

      {isLoading ? (
        <Stack spacing={2}>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rounded" height={120} />
          ))}
        </Stack>
      ) : tickets.length === 0 ? (
        <Card sx={{ p: 6, textAlign: "center", color: "text.secondary" }}>
          No tickets found.
        </Card>
      ) : (
        <Stack spacing={2}>
          {tickets.map((t) => (
            <TicketCard key={t._id} ticket={t} onView={handleView} />
          ))}
        </Stack>
      )}

      {/* Ticket thread dialog */}
      <Dialog open={openDialog} onClose={handleClose} fullWidth maxWidth="sm">
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
            <span>{activeTicket?.subject || "Ticket"}</span>
            <Typography variant="caption" color="text.secondary">
              {activeTicket?.ticketNumber}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" fontWeight={700}>
              {activeTicket?.customer?.name || activeTicket?.contact?.name || "Guest"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {activeTicket?.customer?.email || activeTicket?.contact?.email || ""}
            </Typography>
            {activeTicket?.description && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {activeTicket.description}
              </Typography>
            )}
          </Box>

          <FormControl size="small" sx={{ minWidth: 160, mb: 2 }} disabled={isUpdatingStatus}>
            <InputLabel id="ticket-status-label">Status</InputLabel>
            <Select
              labelId="ticket-status-label"
              label="Status"
              value={activeTicket?.status || "Open"}
              onChange={handleStatusChange}
            >
              <MenuItem value="Open">Open</MenuItem>
              <MenuItem value="Pending">Pending</MenuItem>
              <MenuItem value="Closed">Closed</MenuItem>
            </Select>
          </FormControl>

          <Divider sx={{ mb: 2 }} />

          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1.5,
              maxHeight: 300,
              overflowY: "auto",
              mb: 2,
            }}
          >
            {messages.length === 0 ? (
              <Typography variant="caption" color="text.secondary" align="center">
                No messages yet.
              </Typography>
            ) : (
              messages.map((m, idx) => {
                const isAdmin = m?.sender === "admin";
                return (
                  <Box key={idx} sx={{ alignSelf: isAdmin ? "flex-end" : "flex-start", maxWidth: "80%" }}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        bgcolor: isAdmin
                          ? "primary.main"
                          : alpha(theme.palette.text.primary, 0.06),
                        color: isAdmin ? "primary.contrastText" : "text.primary",
                      }}
                    >
                      <Typography variant="caption" fontWeight={700} display="block">
                        {m?.senderName}
                      </Typography>
                      <Typography variant="body2">{m?.body}</Typography>
                    </Box>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", textAlign: isAdmin ? "right" : "left", mt: 0.25 }}
                    >
                      {formatDateTime(m?.createdAt)}
                    </Typography>
                  </Box>
                );
              })
            )}
          </Box>

          <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}>
            <TextField
              fullWidth
              size="small"
              multiline
              maxRows={4}
              placeholder="Type your reply…"
              value={replyBody}
              onChange={(e) => setReplyBody(e.target.value)}
            />
            <Button
              variant="contained"
              endIcon={<SendIcon />}
              onClick={handleSendReply}
              disabled={isReplying || !replyBody.trim()}
            >
              Send
            </Button>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={3000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.msg}
          </Alert>
        ) : null}
      </Snackbar>
    </PageContainer>
  );
};

export default SupportTicketList;
